import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import waiverService, { WaiverData, WaiverStatusResponse } from '@/services/waiverService';

interface WaiverState {
  activeWaiver: WaiverData | null;
  hasSigned: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: WaiverState = {
  activeWaiver: null,
  hasSigned: true, // Default true so app is not blocked before status loads
  loading: false,
  error: null,
};

export const fetchWaiverStatus = createAsyncThunk(
  'waiver/fetchStatus',
  async (locationId: string, { rejectWithValue }) => {
    try {
      return await waiverService.getWaiverStatus(locationId);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch waiver status');
    }
  },
);

const waiverSlice = createSlice({
  name: 'waiver',
  initialState,
  reducers: {
    markWaiverSigned(state) {
      state.hasSigned = true;
    },
    resetWaiverState(state) {
      state.activeWaiver = null;
      state.hasSigned = true;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaiverStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaiverStatus.fulfilled, (state, action: PayloadAction<WaiverStatusResponse>) => {
        state.loading = false;
        state.activeWaiver = action.payload.waiver;
        state.hasSigned = action.payload.hasSigned;
      })
      .addCase(fetchWaiverStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // On error, do not block — treat as signed to avoid trapping members
        state.hasSigned = true;
      });
  },
});

export const { markWaiverSigned, resetWaiverState } = waiverSlice.actions;
export default waiverSlice.reducer;
